import styled from '@emotion/styled';
import { Tooltip, Typography } from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import {
  CARD_STATUS,
  type DeckReportCard,
  type OperationTable,
} from '../../modules/fluency/deck-report';
import type { FluencyStatusTooltipProp } from '../../views/fluency-dashboard';
import { DataCell } from './components/data-cell';
import { HeaderCell, StyledHeaderCell } from './components/header-cell';
import { getGridDimensions } from './utils';

type Props = {
  operator: string;
  tableFluencyData?: OperationTable[];
  showFluencyStatusTooltip?: FluencyStatusTooltipProp;
};

const TableWrapper = styled.div`
  width: fit-content;
`;

const TooltipContainer = styled.div`
  position: absolute;
  top: calc(50% + 27px);
  left: calc(50% + 14px);
  transform: translate(-50%, -50%);
`;

const TooltipPlaceholder = styled.div``;

const StyledTooltip = styled(Tooltip)`
  width: 220px;
  text-align: center;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const CellsContainer = styled.div`
  position: relative;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 4px;
  gap: 4px;
`;

const LabelCell = styled(Typography.Subtitle1)<{ isLocked?: boolean }>`
  text-align: center;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ isLocked, theme }) =>
    isLocked
      ? `${theme.tokens.color.alias.cm.text['text-disabled'].value}`
      : `${theme.tokens.color.alias.cm.text['text'].value}`};
`;

function TableFluency({
  operator,
  tableFluencyData,
  showFluencyStatusTooltip,
}: Props): JSX.Element {
  const { t } = useTranslation();

  const { allGroupsLocked, noStudentsConnected } =
    showFluencyStatusTooltip as FluencyStatusTooltipProp;

  const tooltipMessage = (() => {
    if (allGroupsLocked) {
      return t('fluency.reports.allGroupsLocked');
    }
    if (noStudentsConnected) {
      return t('fluency.reports.noStudentsConnected');
    }
    return null;
  })();

  const { maxColumns, maxRows, totalRows } = getGridDimensions(
    operator,
    tableFluencyData
  );

  return (
    <TableWrapper>
      <HeaderRow>
        <StyledHeaderCell isLocked={allGroupsLocked}>
          {operator}
        </StyledHeaderCell>
        {Array.from({ length: maxColumns }).map((_, columnIndex) => (
          <HeaderCell
            key={columnIndex}
            column={tableFluencyData?.[columnIndex]}
            columnIndex={columnIndex}
          />
        ))}
      </HeaderRow>
      <CellsContainer>
        <TooltipContainer>
          <StyledTooltip maxWidth={280} content={tooltipMessage} visible>
            <TooltipPlaceholder />
          </StyledTooltip>
        </TooltipContainer>
        {Array.from({ length: maxRows }).map((_, rowIndex) => {
          const operand2 = tableFluencyData?.[rowIndex]?.operand2;
          const rowCards = (tableFluencyData ?? [])
            .map((column) =>
              column.cards.find((card) => card.operand1 === rowIndex + 1)
            )
            .filter((card): card is DeckReportCard => Boolean(card));

          const isRowLocked = rowCards.every(
            (card) => card.status === CARD_STATUS.LOCKED
          );

          return (
            <Row key={`row-${rowIndex}`}>
              <LabelCell isLocked={isRowLocked}>
                {operand2 || rowIndex + 1}
              </LabelCell>
              {Array.from({ length: maxColumns }).map((_, columnIndex) => (
                <DataCell
                  key={`cell-${rowIndex}-${columnIndex}`}
                  column={tableFluencyData?.[columnIndex]}
                  rowIndex={rowIndex}
                  operator={operator}
                  allCellsLocked={allGroupsLocked}
                />
              ))}
            </Row>
          );
        })}
      </CellsContainer>
    </TableWrapper>
  );
}

export default TableFluency;
